import { useCallback, useEffect, useState } from "react";
import GlossaryTooltipContent from "../components/TutorialTooltip/Content/GlossaryTooltipContent";

const useTutorialTooltips = (
  lessonNum,
  currSlideId,
  openModal,
  currSlideHasModal,
  /*
  unique event flags that can trigger a tutorial tooltip
  NOTE: if variable names passed in get changed, keys in EVENT_TRIGGER_FLAG_KEYS
  MUST get updated accordingly
  */
  eventTriggerFlags
) => {
  const [tutorialsCompleted, setTutorialsCompleted] = useState(
    INITIAL_INCOMPLETE_TUTORIALS_STATE
  );
  const [showTooltip, setShowTooltip] = useState(false);
  /*
  startModalShown is used to track when the start modal has been shown
  (if current slide has modal) for the purposes of the tooltip flow only. 
  it should not be used in any way to actually determine if the start modal is being
  shown since it updates when any timer modal is being shown regardless of phase.
  */
  const [startModalShown, setStartModalShown] = useState(false);

  const { tutorialKey, eventTriggerFlagKey } = useCurrTutorialData(
    lessonNum,
    currSlideId,
    tutorialsCompleted
  );
  const slideHasIncompleteTutorial = !!tutorialKey;
  const activeTutorialId = showTooltip ? TUTORIALS[tutorialKey]?.id : null;
  const isTutorialTooltipBlockingInputs =
    (!eventTriggerFlagKey && slideHasIncompleteTutorial) ||
    (eventTriggerFlagKey && eventTriggerFlags[eventTriggerFlagKey]);

  const completeActiveTutorial = useCallback(() => {
    if (TUTORIALS[tutorialKey]) {
      setTutorialsCompleted((prev) => ({
        ...prev,
        [tutorialKey]: true,
      }));
      setShowTooltip(false);
    }
  }, [tutorialKey]);

  /*
  necessary since openModal takes time to update to true on initial slide change so we can't just check
  if openModal is false to determine whether to show the tooltip since it probably will be false if
  going from a slide that wasn't showing a modal when the slide was changed. this is the first step in 
  determining when the tooltip is ready to be shown for slides that have timer modals since the tooltips
  need to be shown after the start modal.
  */
  useEffect(() => {
    if (openModal && slideHasIncompleteTutorial) {
      setStartModalShown(true);
    } else if (!slideHasIncompleteTutorial) {
      setStartModalShown(false);
    }
  }, [openModal, slideHasIncompleteTutorial]);

  useEffect(() => {
    if (
      ((startModalShown && !openModal) || !currSlideHasModal) &&
      slideHasIncompleteTutorial &&
      (!eventTriggerFlagKey || eventTriggerFlags[eventTriggerFlagKey])
    ) {
      setShowTooltip(true);
    }
  }, [
    startModalShown,
    openModal,
    currSlideHasModal,
    slideHasIncompleteTutorial,
    eventTriggerFlagKey,
    eventTriggerFlags,
  ]);

  return [
    activeTutorialId,
    isTutorialTooltipBlockingInputs,
    completeActiveTutorial,
  ];
};

export default useTutorialTooltips;

const DEFAULT_TUTORIAL_TOOLTIP_CONFIG = {
  secondaryBtnLabel: "Dismiss",
};

/*
NOTE: content of config objects can be an element or a string, 
if its a string OR a React.Fragment element it will be wrapped
with <p className='default-tooltip-text'></p> in TutorialTooltip.js.
*/
export const TUTORIAL_TOOLTIP_CONFIGS = {
  STUDENT_STATUS: {
    primaryBtnLabel: "Got it",
    id: "studentStatus",
    title: "Students are playing!",
    content: (
      <>
        View if student screens are <i>paused</i>, <i>playing</i>, or{" "}
        <i>polling</i> here.
      </>
    ),
  },
  GLOSSARY: {
    ...DEFAULT_TUTORIAL_TOOLTIP_CONFIG,
    id: "glossary",
    title: "Check the glossary for...",
    content: <GlossaryTooltipContent />,
    primaryBtnLabel: "Check it out",
  },
};

const TUTORIAL_SLIDE_IDS = {
  PLAYTIME_EXPLORE: "playtime-explore",
  PLAYTIME_ELABORATE: "playtime-keepgoing",
};

const EVENT_TRIGGER_FLAG_KEYS = {
  areStudentsPlaying: "areStudentsPlaying",
  isGamePlaying: "isGamePlaying",
};

const EVENT_TRIGGER_FLAG_KEYS_ARR = Object.keys(EVENT_TRIGGER_FLAG_KEYS);

// NOTE: keys must be structured in the format of 'lessonNum-slideId' or 'lessonNum-slideId-eventTriggerKey'
const TUTORIALS = {
  [`1-${TUTORIAL_SLIDE_IDS.PLAYTIME_EXPLORE}-${EVENT_TRIGGER_FLAG_KEYS.areStudentsPlaying}`]:
    TUTORIAL_TOOLTIP_CONFIGS.STUDENT_STATUS,
  [`1-${TUTORIAL_SLIDE_IDS.PLAYTIME_ELABORATE}-${EVENT_TRIGGER_FLAG_KEYS.isGamePlaying}`]:
    TUTORIAL_TOOLTIP_CONFIGS.GLOSSARY,
  [`2-${TUTORIAL_SLIDE_IDS.PLAYTIME_EXPLORE}-${EVENT_TRIGGER_FLAG_KEYS.areStudentsPlaying}`]:
    TUTORIAL_TOOLTIP_CONFIGS.STUDENT_STATUS,
  [`3-${TUTORIAL_SLIDE_IDS.PLAYTIME_ELABORATE}-${EVENT_TRIGGER_FLAG_KEYS.isGamePlaying}`]:
    TUTORIAL_TOOLTIP_CONFIGS.GLOSSARY,
};

const INITIAL_INCOMPLETE_TUTORIALS_STATE = Object.keys(TUTORIALS).reduce(
  (acObj, tutorialId) => {
    acObj[tutorialId] = false;
    return acObj;
  },
  {}
);

// UTILITY FUNCTIONS
const isTutorialKeyValidAndIncomplete = (key, tutorialsCompleted) =>
  TUTORIALS[key] && !tutorialsCompleted[key];

/**
 * @function useCurrTutorialData
 * @returns {Object} with tutorialKey property if a valid and incomplete tutorial is found for current lesson
 * and slide (and maybe event trigger in which case eventTriggerFlagKey will also be returned), otherwise returns
 * an empty object.
 */
function useCurrTutorialData(lessonNum, currSlideId, tutorialsCompleted) {
  const baseTutorialKey = `${lessonNum}-${currSlideId}`;
  if (isTutorialKeyValidAndIncomplete(baseTutorialKey, tutorialsCompleted)) {
    return { tutorialKey: baseTutorialKey };
  }

  // will store last tutorial key checked to use if a valid and incomplete trigger event tutorial is found
  let eventTriggerTutorialKey;
  const eventTriggerFlagKey = EVENT_TRIGGER_FLAG_KEYS_ARR.find((flagKey) => {
    eventTriggerTutorialKey = `${baseTutorialKey}-${flagKey}`;
    return isTutorialKeyValidAndIncomplete(
      eventTriggerTutorialKey,
      tutorialsCompleted
    );
  });
  return eventTriggerFlagKey
    ? { tutorialKey: eventTriggerTutorialKey, eventTriggerFlagKey }
    : {};
}
